import { vatInside } from './tax'

/**
 * The RS.GE tax invoice (ანგარიშ-ფაქტურა) made from a sale's commercial
 * invoice (096) — the arithmetic, pure and shared by the server that files
 * it and the screen that shows it. The SOAP lives in lib/rsge-invoice.ts.
 *
 * What RS.GE takes per line is a name, a unit, a quantity, the amount WITH
 * VAT and the VAT inside it (`save_invoice_desc`). What the shop was paid is
 * `sale.total`, after the sale-wide discount and any bonus; the lines on
 * the paper are list prices before them. So the difference is spread over
 * the lines in proportion to their amount, and the last line takes the
 * rounding tetri — Σ lines is always exactly the sale's total, which is the
 * figure the buyer's VAT credit must agree with.
 *
 * Facts from the wire (2026-09-26, the RS.GE test org, memory rsge.md):
 * RS.GE computes the VAT itself (`SDRG_AMOUNT`) and it equals 18/118 of the
 * line; line ids are past int32, so a saved line cannot be edited or
 * deleted through the API and `save_invoice_desc` answers `false` while
 * saving — the only proof a line is there is reading it back
 * (`matchLines`).
 */

export type TaxLine = {
  goods: string
  unit: string
  qty: number
  /** The line's amount WITH VAT, after its share of the sale discount. */
  full: number
  /** VAT inside `full` (18/118). */
  vat: number
}

/** A line as RS.GE reads it back (`get_invoice_desc`). */
export type RsLine = { goods: string; unit: string; qty: number; full: number; vat: number }

export type SoldLine = { name: string; qty: number; amount: number; unit?: string | null }

export type TaxLinesResult =
  | { ok: true; lines: TaxLine[]; total: number }
  | { ok: false; reason: string }

const r2 = (n: number) => Math.round(n * 100) / 100

/** What `products.unit` really holds, as the document says it. */
const TAX_UNIT: Record<string, string> = { piece: 'ცალი', kg: 'კგ', m: 'მ', l: 'ლ' }

/** The unit RS.GE prints; an empty one is a piece. 1.75: this took „ც" / „კგ" for what
 *  a product stores, so a real invoice said „piece" or „kg". matchLines never reads the unit. */
export function taxUnit(unit: string | null | undefined): string {
  const u = (unit ?? '').trim()
  if (!u || u === 'ც' || u === 'ც.' || u === 'pcs') return 'ცალი'
  return TAX_UNIT[u] ?? u.slice(0, 20)
}

/**
 * The lines to file. `sold` are the sale's paid lines (₾0 ride-alongs such
 * as a coffee cup are left out, as on the commercial invoice); `total` is
 * what the buyer paid for the sale.
 */
export function buildTaxLines(sold: SoldLine[], total: number): TaxLinesResult {
  const lines = sold.filter((l) => l.amount > 0)
  if (lines.length === 0) return { ok: false, reason: 'ინვოისში ფასიანი ხაზი არ არის' }
  if (lines.some((l) => !(l.qty > 0))) return { ok: false, reason: 'ხაზის რაოდენობა ნულია ან უარყოფითი' }
  const t = r2(total)
  if (!(t > 0)) return { ok: false, reason: 'გაყიდვის ჯამი ნულია' }
  const subtotal = r2(lines.reduce((s, l) => s + l.amount, 0))
  if (t > subtotal + 0.001) return { ok: false, reason: 'გაყიდვის ჯამი ხაზების ჯამს აღემატება' }

  const factor = t / subtotal
  const out: TaxLine[] = lines.map((l) => {
    const full = r2(l.amount * factor)
    return { goods: l.name.trim().slice(0, 200) || '—', unit: taxUnit(l.unit), qty: l.qty, full, vat: 0 }
  })
  // The rounding tetri go on the largest line, where they are the smallest
  // share — never below zero.
  const drift = r2(t - out.reduce((s, l) => s + l.full, 0))
  if (drift !== 0) {
    const big = out.reduce((b, l, i) => (l.full > out[b].full ? i : b), 0)
    out[big].full = r2(out[big].full + drift)
  }
  if (out.some((l) => !(l.full > 0))) return { ok: false, reason: 'ფასდაკლების შემდეგ ხაზის თანხა ნული გამოვიდა' }
  for (const l of out) l.vat = r2(vatInside(l.full))
  return { ok: true, lines: out, total: t }
}

/**
 * Did RS.GE keep exactly what we sent? Order-free, one-to-one: every line of
 * ours must find a line of theirs with the same goods, quantity and amounts
 * (to the tetri). Extra lines on their side fail it too — a retry that
 * doubled a line is exactly what this must catch.
 */
export function matchLines(ours: TaxLine[], theirs: RsLine[]): { ok: boolean; missing: number; extra: number } {
  const left = [...theirs]
  let missing = 0
  for (const o of ours) {
    const i = left.findIndex((t) =>
      t.goods.trim() === o.goods.trim() &&
      Math.abs(t.qty - o.qty) < 0.0005 &&
      Math.abs(t.full - o.full) < 0.005 &&
      Math.abs(t.vat - o.vat) < 0.005)
    if (i === -1) missing++
    else left.splice(i, 1)
  }
  return { ok: missing === 0 && left.length === 0, missing, extra: left.length }
}

/** RS.GE's invoice statuses (the protocol, memory rsge.md). */
export const TAX_INVOICE_STATUS: Record<number, string> = {
  0: 'პროექტი',
  1: 'გაგზავნილი',
  2: 'დადასტურებული',
  3: 'კორექტირებული',
  4: 'კორექტირება — ახალი',
  5: 'კორექტირება გაგზავნილი',
  6: 'გაუქმებული',
  7: 'გაუქმება დადასტურებული',
  8: 'კორექტირება დადასტურებული',
}

export const taxInvoiceStatusLabel = (s: number | null | undefined) =>
  s == null ? '—' : (TAX_INVOICE_STATUS[s] ?? `სტატუსი ${s}`)

/**
 * `ეა-86 №7857557` once sent. A DRAFT's series is not shown: RS.GE gives a
 * draft a placeholder one (`ეა-01`) and the real series with the number at
 * sending (2026-09-26: draft ეა-01 → sent ეა-86 №7857557).
 */
export function taxInvoiceNo(series: string | null | undefined, number: number | null | undefined): string {
  const s = (series ?? '').trim()
  if (number != null && number > 0) return `${s ? s + ' ' : ''}№${number}`
  return 'სერია და ნომერი — გაგზავნისას'
}

/** The 9/11-digit code RS.GE knows a taxpayer by. */
export const isTin = (tin: string | null | undefined) => /^(\d{9}|\d{11})$/.test((tin ?? '').trim())

/** The operation date as RS.GE wants it: Tbilisi wall-clock, no zone. */
export function rsDateTime(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Tbilisi', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).formatToParts(d)
  const p = (t: string) => parts.find((x) => x.type === t)?.value ?? '00'
  const hh = p('hour') === '24' ? '00' : p('hour')
  return `${p('year')}-${p('month')}-${p('day')}T${hh}:${p('minute')}:${p('second')}`
}
